import { useEffect, useRef, useState } from 'react';
import { useSetAuthTokenMutation } from '../../../api/serverStream';
import {
  SSEEvent,
  BatchProgress,
  BatchSummary,
  BatchStatus,
  AnswerData,
  Citation,
  RetrievedChunk,
  StatusCounts,
  ProcessingStats,
  StructuredError,
  DocumentMetadata
} from '../../../api/ai-supervisor';
import config from '../../../config';

interface UseBatchSSEParams {
  initiativeId: string;
  surveyId: string;
  batchId?: string;
  enabled?: boolean;
}

export interface QuestionState {
  utrvId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  stage?: 'retrieved' | 'answered' | 'cited' | 'validated' | 'quality_checked' | 'reconciled';
  result?: {
    answer?: AnswerData;
    citations?: Citation[];
    retrievedChunks?: RetrievedChunk[];
    qualityScore?: number;
    confidenceScore?: number;
    validationStatus?: 'valid' | 'invalid' | 'warning';
    processingTime: number;
  };
  error?: StructuredError; // Now structured error object
  errorMessage?: string; // Deprecated fallback
}

interface SSEState {
  connected: boolean;
  status: BatchStatus | null;
  progress: BatchProgress | null;
  summary: BatchSummary | null;
  counts: StatusCounts | null; // NEW - server-computed status counts
  stats: ProcessingStats | null; // NEW - server-computed processing stats
  model: string | null; // AI model used for processing
  error: string | null;
  reconnecting: boolean;
  questions: Map<string, QuestionState>;
  documentCount?: number;
  documentMetadata?: Record<string, DocumentMetadata>;
}

export const useBatchSSE = ({ initiativeId, surveyId, batchId, enabled = true }: UseBatchSSEParams) => {
  const [state, setState] = useState<SSEState>({
    connected: false,
    status: null,
    progress: null,
    summary: null,
    counts: null,
    stats: null,
    model: null,
    error: null,
    reconnecting: false,
    questions: new Map(),
  });

  // Reset state when batchId changes to prevent stale data from previous batch
  useEffect(() => {
    setState({
      connected: false,
      status: null,
      progress: null,
      summary: null,
      counts: null,
      stats: null,
      model: null,
      error: null,
      reconnecting: false,
      questions: new Map(),
      documentCount: undefined,
    });
  }, [batchId]);

  const [setAuthToken] = useSetAuthTokenMutation();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const cleanup = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  };

  const connect = async () => {
    if (!batchId || !enabled) return;

    cleanup();

    try {
      await setAuthToken();
    } catch (tokenError) {
      console.error('Failed to set auth token for batch SSE:', tokenError);
      setState((prev) => ({
        ...prev,
        error: 'Unable to authenticate batch stream. Please refresh the page.',
        reconnecting: false,
      }));
      return;
    }

    const streamUrl = `${config.apiUrl}initiatives/${initiativeId}/ai-supervisor/streaming/survey/${surveyId}/batch/${batchId}`;
    const eventSource = new EventSource(streamUrl, { withCredentials: true });
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setState((prev) => ({ ...prev, connected: true, reconnecting: false, error: null }));
      reconnectAttemptsRef.current = 0;
    };

    eventSource.onmessage = (event) => {
      try {
        const data: SSEEvent = JSON.parse(event.data);

        switch (data.type) {
          case 'connected':
            setState((prev) => {
              // Restore question states from persisted data
              const restoredQuestions = new Map(prev.questions);

              if (data.currentState.questionStatuses) {
                Object.entries(data.currentState.questionStatuses).forEach(([utrvId, status]) => {
                  // Only restore if we don't already have real-time data for this question
                  if (!restoredQuestions.has(utrvId)) {
                    restoredQuestions.set(utrvId, {
                      utrvId,
                      status: status as 'pending' | 'processing' | 'completed' | 'failed',
                      // We don't have stage/result data in persisted state, will be updated by subsequent SSE events
                    });
                  }
                });
              }

              return {
                ...prev,
                connected: true,
                status: data.currentState.status,
                progress: data.currentState.progress,
                summary: data.currentState.summary || null,
                questions: restoredQuestions,
                documentMetadata: data.currentState.documentMetadata, // Receive document metadata once at connection
              };
            });
            break;

          case 'progress':
            setState((prev) => ({
              ...prev,
              progress: data.progress,
              status: data.status, // Now always provided by server
              counts: data.counts, // Server-computed status counts
              stats: data.stats || null, // Optional processing statistics
              model: data.model || prev.model, // AI model used for processing
            }));
            break;

          case 'status-changed':
            // Immediate status change notification (pause/resume/abort)
            setState((prev) => ({
              ...prev,
              status: data.status,
            }));
            break;

          case 'question-update':
            setState((prev) => {
              const newQuestions = new Map(prev.questions);
              const utrvId = data.utrvId;
              newQuestions.set(utrvId, data);
              return {
                ...prev,
                questions: newQuestions,
              };
            });
            break;

          case 'paused':
            setState((prev) => ({
              ...prev,
              status: 'paused',
              // Update progress with current stats if provided
              progress: data.progress || prev.progress,
              // Update summary with current stats if provided
              summary: data.summary || prev.summary,
              // Capture document count from pause event
              documentCount: data.documentCount,
              // Capture document metadata from pause event
              documentMetadata: data.documentMetadata || prev.documentMetadata,
            }));
            // Keep connection open for potential resume
            break;

          case 'completed':
            setState((prev) => ({
              ...prev,
              status: 'completed',
              // Backend now sends normalized summary with correct field names
              summary: data.summary || prev.summary,
              // Update progress with final stats if provided
              progress: data.progress || prev.progress,
              // Capture document count from completion event
              documentCount: data.documentCount,
              // Capture document metadata from completion event
              documentMetadata: data.documentMetadata || prev.documentMetadata,
            }));
            // Close connection on completion
            cleanup();
            break;

          case 'failed':
            setState((prev) => ({
              ...prev,
              status: 'failed',
              error: data.error,
              // Update progress with final stats if provided
              progress: data.progress || prev.progress,
              // Backend now sends normalized summary with correct field names
              summary: data.summary || prev.summary,
              // Capture document count from failure event
              documentCount: data.documentCount,
              // Capture document metadata from failure event
              documentMetadata: data.documentMetadata || prev.documentMetadata,
            }));
            // Close connection on failure
            cleanup();
            break;
        }
      } catch (error) {
        console.error('Failed to parse SSE message:', error);
      }
    };

    eventSource.onerror = () => {
      setState((prev) => ({ ...prev, connected: false }));
      cleanup();

      // Attempt to reconnect with exponential backoff
      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        setState((prev) => ({ ...prev, reconnecting: true }));
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
        reconnectAttemptsRef.current++;

        reconnectTimeoutRef.current = setTimeout(() => {
          void connect();
        }, delay);
      } else {
        setState((prev) => ({
          ...prev,
          error: 'Failed to connect to batch stream. Please refresh the page.',
          reconnecting: false,
        }));
      }
    };
  };

  useEffect(() => {
    if (enabled && batchId) {
      connect().catch((error) => {
        console.error('Failed to connect to batch stream:', error);
      });
    }

    return () => {
      cleanup();
    };
  }, [batchId, enabled, initiativeId, surveyId]);

  return {
    ...state,
    reconnect: () => {
      connect().catch((error) => {
        console.error('Failed to reconnect to batch stream:', error);
      });
    },
  };
};
